import React, { Suspense, useState } from 'react';
import { Layout, Tabs, Modal, ConfigProvider, theme, Button, message } from 'antd';
import { SettingOutlined, WarningOutlined } from '@ant-design/icons';
import AppContext from './context/AppContext';
import AppProvider from './context/AppProvider';
import Search from './components/Search';
import ShoppingList from './components/ShoppingList';
import History from './components/History';
import Settings from './components/Settings';

const { Header, Content } = Layout;

const AppContent = () => {
  const { state, dispatch } = React.useContext(AppContext);
  const [currentPage, setCurrentPage] = React.useState(state.shoppingMode ? 'shopping-list' : 'search');
  const [settingsVisible, setSettingsVisible] = useState(false);

  const hasDuplicates = React.useMemo(() => {
    const itemCounts = {};
    let duplicatesExist = false;
    
    state.shoppingList.forEach(item => {
      const key = `${item.name}-${item.measure}`;
      if (itemCounts[key]) {
        duplicatesExist = true;
      } else {
        itemCounts[key] = 1;
      }
    });
    
    return duplicatesExist;
  }, [state.shoppingList]);

  React.useEffect(() => {
    if (state.shoppingMode) {
      setCurrentPage('shopping-list');
    }
  }, [state.shoppingMode]);

  const handleTabChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning!';
    if (hour < 18) return 'Good Afternoon!';
    return 'Good Evening!';
  };

  const headerStyle = {
    backgroundColor: state.theme === 'light' ? '#fff' : '#141414',
    padding: '16px 24px',
    paddingBottom: state.shoppingMode ? '16px' : '0px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: 'auto'
  };

  const topBarStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0px'
  };

  const greetingStyle = {
    margin: 0,
    fontFamily: 'Nunito',
    fontWeight: 700,
    fontSize: '28px'
  };

  const items = [
    ...(state.shoppingMode ? [] : [{
      key: 'search',
      label: 'HOME'
    }]),
    {
      key: 'shopping-list',
      label: 'SHOPPING'
    },
    {
      key: 'history',
      label: 'HISTORY'
    }
  ];

  const renderContent = () => {
    switch (currentPage) {
      case 'search':
        return <Search />;
      case 'shopping-list':
        return <ShoppingList />;
      case 'history':
        return <History />;
      default:
        return <Search />;
    }
  };

  const handleRemoveDuplicates = () => {
    dispatch({
      type: 'REMOVE_DUPLICATES'
    });
    message.success('Duplicate items removed');
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: state.themeColor,
        },
        algorithm: state.theme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
      }}
    >
      <Layout style={{ 
        background: state.theme === 'light' ? '#fff' : '#141414',
        minHeight: '100vh'
      }}>
        <Header style={headerStyle}>
          <div style={topBarStyle}>
            <h1 style={greetingStyle}>{getGreeting()}</h1>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {state.shoppingMode && state.shoppingList.length > 0 && hasDuplicates && (
                <Button 
                  type="primary"
                  danger
                  onClick={handleRemoveDuplicates}
                  icon={<WarningOutlined />}
                  size="small"
                >
                  Remove Duplicates
                </Button>
              )}
              <SettingOutlined 
                style={{ 
                  fontSize: '24px',
                  cursor: 'pointer'
                }}
                onClick={() => setSettingsVisible(true)}
              />
            </div>
          </div>
          {!state.shoppingMode && (
            <Tabs
              activeKey={currentPage}
              onChange={handleTabChange}
              items={items}
              centered
              style={{
                fontFamily: 'Nunito',
                fontWeight: 600,
                letterSpacing: '0.5px'
              }}
            />
          )}
        </Header>
        <Content style={{ padding: '0px 1px' }}>
          <Suspense fallback={<div>Loading...</div>}>
            {state.shoppingMode ? (
              <ShoppingList />
            ) : (
              renderContent()
            )}
          </Suspense>
        </Content>

        <Modal
          title={null}
          open={settingsVisible}
          onCancel={() => setSettingsVisible(false)}
          footer={null}
          style={{ top: 0 }}
          width="100%"
          height="100%"
          getContainer={'body'}
          styles={{
            body: { 
              padding: 0,
              overflow: 'hidden'
            },
            mask: {
              backgroundColor: state.theme === 'light' ? '#fff' : '#141414'
            },
            content: {
              padding: 0,
              boxShadow: 'none'
            }
          }}
          closable={true}
        >
          <Settings onClose={() => setSettingsVisible(false)} />
        </Modal>
      </Layout>
    </ConfigProvider>
  );
};

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App; 